import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { VesselMessage } from '../../../types/VesselMessage';
import { CenteredPagination } from '../../cdl/Pagination/CenteredPagination';
import { StatePlaceholder } from '../../cdl/StatePlaceholder/StatePlaceholder';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { IconAnchor } from '../../common/icons/cdl/Anchor';
import { IconCheck } from '../../common/icons/cdl/Check';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../common/ui/Box';
import { useVesselPagination } from '../useVesselPagination';

import { VesselStatus } from './types/VesselStatus';
import { useVesselOverviewFilters } from './useVesselOverviewFilters';

export const VesselOverviewTable = () => {
    const search = useSearch({ from: '/_app/vessels' });
    const navigate = useNavigate({ from: '/vessels' });

    const role = useRole();
    const active = search.tab === VesselStatus.ACTIVE;

    const filters = useVesselOverviewFilters();
    const vesselPaginationQuery = useVesselPagination({ ...filters, page: search.page, active });

    if (vesselPaginationQuery.isPending) {
        return <LoadingIndicator />;
    }
    if (vesselPaginationQuery.isError) {
        return <AdvancedError />;
    }
    if (!vesselPaginationQuery.data.content.length) {
        return (
            <Box marginTop="124px" display="flex" justifyContent="center">
                <StatePlaceholder
                    Icon={IconAnchor}
                    title={translate('vessel.emptylist')}
                    description={translate('vessel.noResults.description')}
                />
            </Box>
        );
    }

    const buildLinkOptions = (vessel: VesselMessage) => {
        return linkOptions({
            to: '/vessel/$id',
            params: {
                id: vessel.id,
            },
        });
    };

    return (
        <div>
            <TableBuilder data={vesselPaginationQuery.data.content} rowLink={buildLinkOptions} grow={false}>
                <TableBuilderColumn<VesselMessage> header={translate('vessel.name')} width="25%" truncate>
                    {(vessel) => vessel.name}
                </TableBuilderColumn>
                <TableBuilderColumn<VesselMessage> header={translate('vessel.imo')} width="15%">
                    {(vessel) => vessel.imo}
                </TableBuilderColumn>
                {role.isSuperAdmin() || !role.isOneCompanyUser() ? (
                    <TableBuilderColumn<VesselMessage> header={translate('vessel.company')} width="25%" truncate>
                        {(vessel) =>
                            vessel.customer
                                ? formatCompanyName({
                                      company: vessel.customer,
                                  })
                                : ''
                        }
                    </TableBuilderColumn>
                ) : null}
                <TableBuilderColumn<VesselMessage> header={translate('vessel.defaultsupplier')} width="15%" truncate>
                    {(vessel) => vessel.defaultSupplier?.name || ''}
                </TableBuilderColumn>
                <TableBuilderColumn<VesselMessage> header={translate('vessel.defaultContract')} width="15%" truncate>
                    {(vessel) => vessel.defaultContract?.name || ''}
                </TableBuilderColumn>
                {role.isSuperAdmin() ? (
                    <TableBuilderColumn<VesselMessage> header={translate('vessel.paying')}>
                        {(vessel) => (vessel.paying ? <IconCheck width={16} height={16} /> : null)}
                    </TableBuilderColumn>
                ) : null}
            </TableBuilder>
            <CenteredPagination
                pageTotal={vesselPaginationQuery.pageTotal}
                currentPage={search.page}
                onPageChange={(page) => {
                    navigate({ search: (previousSearch) => ({ ...previousSearch, page }) });
                }}
            />
        </div>
    );
};
